import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogIn, Layout, Bug,
  Navigation, Database, Zap,
  Sun, Moon, RefreshCw,
  HardDrive, Trash2, Eraser, Loader2, LogOut,
} from 'lucide-react';
import { MODULE_ITEMS, isPathMatch } from '../data/navigation';
import { useAppState } from '../hooks/useAppState';
import { logoutApi } from '../services/api';

/**
 * DevToolbar — CI4-style collapsible developer debug bar.
 *
 * - Collapses to an Excel-style tab at the bottom-right corner.
 * - The trigger tab is ALWAYS visible (never hidden when expanded).
 * - Navigating between routes does NOT close the toolbar.
 * - State (open/closed) is persisted via useAppState → localStorage.
 * - Theme toggle (light/dark), force-refresh, and logout live in the header.
 *
 * Visible only for the super admin / debugger roles (canAccessDevToolbar).
 * See docs/MODULES_MAP.md > Module 10: Settings & Roles.
 */

// ─── Route Config (derived from the module navigation config) ─────────
const DEV_ROUTES = [
  {
    path: '/login',
    label: 'Auth / Login',
    icon: LogIn,
    description: 'Authentication & Login Page',
    match: (path) => path === '/login' || path === '/' || path === '',
  },
  {
    path: '/showcase',
    label: 'Design System',
    icon: Layout,
    description: 'Component Library & Design Tokens Showcase',
    match: (path) => path === '/showcase',
  },
  ...MODULE_ITEMS.map((item) => ({
    path: item.path,
    label: item.label,
    icon: item.icon,
    description: item.description,
    match: (path) => isPathMatch(path, item.path),
  })),
];

// ─── Tab Config ──────────────────────────────────────────────────────────────
const DEV_TABS = [
  { id: 'navigation',  label: 'Navigation',   icon: Navigation, available: true  },
  { id: 'storage',     label: 'Storage',      icon: HardDrive,  available: true  },
  { id: 'queries',     label: 'Queries',       icon: Database,   available: false },
  { id: 'performance', label: 'Performance',   icon: Zap,        available: false },
];

// ─── Navigation Tab Panel ────────────────────────────────────────────────────
function TabNavigation({ currentPath, navigateTo }) {
  return (
    <div className="devtoolbar__tab-content">
      <div className="devtoolbar__tab-header">
        <span className="devtoolbar__tab-title">SPA Route Switcher</span>
        <span className="devtoolbar__tab-meta">{DEV_ROUTES.length} routes registered</span>
      </div>
      <div className="devtoolbar__route-list">
        {DEV_ROUTES.map((route) => {
          const isActive = route.match(currentPath);
          const Icon = route.icon;
          return (
            <button
              key={route.path}
              type="button"
              className={`devtoolbar__route-item${isActive ? ' devtoolbar__route-item--active' : ''}`}
              onClick={() => navigateTo(route.path)}
              // ↑ No onClose() — toolbar stays open on navigation
            >
              <span className="route-item__icon"><Icon size={15} /></span>
              <span className="route-item__info">
                <span className="route-item__label">{route.label}</span>
                <span className="route-item__desc">{route.description}</span>
              </span>
              <span className="route-item__path">{route.path}</span>
              {isActive && <span className="route-item__badge">active</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Coming Soon Panel ───────────────────────────────────────────────────────
function TabComingSoon({ tab }) {
  const Icon = tab.icon;
  return (
    <div className="devtoolbar__tab-content devtoolbar__tab-content--empty">
      <Icon size={28} className="empty-icon" />
      <span className="empty-label">{tab.label} — coming soon</span>
      <span className="empty-desc">
        This debug panel will be implemented in a future release.
      </span>
    </div>
  );
}

// ─── Storage Panel ───────────────────────────────────────────────────────────
const STORAGE_PREFIX = 'bursaku_';

function countSiteKeys() {
  let count = 0;
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) count += 1;
    }
  } catch {
    // localStorage unavailable
  }
  return count;
}

function flushSiteLocalStorage() {
  let removed = 0;
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    for (const key of keys) {
      localStorage.removeItem(key);
      removed += 1;
    }
  } catch {
    // localStorage unavailable
  }
  return removed;
}

async function clearSiteCache() {
  let cleared = 0;
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.clear();
    if (typeof caches !== 'undefined') {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
      cleared = names.length;
    }
  } catch {
    // cache API unavailable / error
  }
  return cleared;
}

function TabStorage() {
  const [keyCount, setKeyCount] = useState(countSiteKeys);
  const [running, setRunning] = useState(null); // 'local' | 'cache'
  const [status, setStatus] = useState(null);

  const refreshCount = () => setKeyCount(countSiteKeys());

  const handleFlush = () => {
    if (running) return;
    const ok = window.confirm(
      'Flush localStorage?\n\nHapus semua data bursaku_* (theme, navbar, finance records, dll) untuk situs ini saja?',
    );
    if (!ok) return;
    setRunning('local');
    setStatus(null);
    // Let the running state paint before the blocking flush.
    setTimeout(() => {
      const removed = flushSiteLocalStorage();
      refreshCount();
      setRunning(null);
      setStatus(`Dibersihkan ${removed} key localStorage. Memuat ulang…`);
      window.location.reload();
    }, 30);
  };

  const handleClearCache = () => {
    if (running) return;
    const ok = window.confirm(
      'Clear site cache?\n\nHapus cache (Cache Storage + sessionStorage) untuk situs ini saja?',
    );
    if (!ok) return;
    setRunning('cache');
    setStatus(null);
    clearSiteCache().then((cleared) => {
      setRunning(null);
      setStatus(
        `Cache dibersihkan (${cleared} cache entry, sessionStorage kosong). Memuat ulang…`,
      );
      window.location.reload();
    });
  };

  return (
    <div className="devtoolbar__tab-content devtoolbar__storage">
      <div className="devtoolbar__tab-header">
        <span className="devtoolbar__tab-title">Storage &amp; Cache</span>
        <span className="devtoolbar__tab-meta">{keyCount} bursaku_* key</span>
      </div>

      <div className="devtoolbar__storage-row">
        <div className="devtoolbar__storage-info">
          <span className="devtoolbar__storage-title">Flush localStorage</span>
          <span className="devtoolbar__storage-desc">
            Hapus semua key <code>bursaku_*</code> (theme, navbar, finance records) — hanya situs ini.
          </span>
        </div>
        <button
          type="button"
          className="devtoolbar__storage-btn devtoolbar__storage-btn--danger"
          onClick={handleFlush}
          disabled={Boolean(running)}
          title="Hapus localStorage bursaku_* lalu reload"
        >
          {running === 'local' ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />}
          {running === 'local' ? 'Menghapus…' : 'Flush'}
        </button>
      </div>

      <div className="devtoolbar__storage-row">
        <div className="devtoolbar__storage-info">
          <span className="devtoolbar__storage-title">Clear site cache</span>
          <span className="devtoolbar__storage-desc">
            Hapus Cache Storage dan sessionStorage origin ini — hanya situs ini.
          </span>
        </div>
        <button
          type="button"
          className="devtoolbar__storage-btn devtoolbar__storage-btn--danger"
          onClick={handleClearCache}
          disabled={Boolean(running)}
          title="Hapus cache origin ini lalu reload"
        >
          {running === 'cache' ? <Loader2 size={14} className="spin" /> : <Eraser size={14} />}
          {running === 'cache' ? 'Menghapus…' : 'Clear'}
        </button>
      </div>

      {status && <span className="devtoolbar__storage-status">{status}</span>}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function DevToolbar({
  currentPath,
  navigateTo,
  isOpen,
  onToggle,
  theme,
  onToggleTheme,
  isDeveloper = true,
}) {
  const navigate = useNavigate();
  const { setCurrentUserUsername } = useAppState();
  const [activeTab, setActiveTab] = useState('navigation');
  const currentTab = DEV_TABS.find((t) => t.id === activeTab);

  if (!isDeveloper) return null;

  const handleLogout = async () => {
    await logoutApi();
    setCurrentUserUsername('');
    navigate('/login');
  };

  return (
    <div className={`devtoolbar${isOpen ? ' devtoolbar--open' : ''}`}>

      {/* ── Expanded Panel (sits above the trigger tab) ─────────────── */}
      {isOpen && (
        <div className="devtoolbar__panel">

          {/* Panel Header */}
          <div className="devtoolbar__panel-header">
            <div className="devtoolbar__panel-brand">
              <span className="dev-dot-pulse" />
              <span className="devtoolbar__panel-title">Dev Toolbar</span>
              <span className="devtoolbar__panel-env">development</span>
            </div>

            {/* Header Actions */}
            <div className="devtoolbar__header-actions">
              {/* Theme Toggle */}
              <button
                type="button"
                className="devtoolbar__action-btn"
                onClick={onToggleTheme}
                title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
              </button>

              {/* Force Refresh */}
              <button
                type="button"
                className="devtoolbar__action-btn"
                onClick={() => window.location.reload()}
                title="Force Reload Page"
                aria-label="Force reload"
              >
                <RefreshCw size={14} />
              </button>

              {/* Logout */}
              <button
                type="button"
                className="devtoolbar__action-btn devtoolbar__action-btn--danger"
                onClick={handleLogout}
                title="Logout"
                aria-label="Logout"
              >
                <LogOut size={14} />
              </button>
            </div>
          </div>

          {/* Tab Bar */}
          <div className="devtoolbar__tabs" role="tablist">
            {DEV_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  type="button"
                  aria-selected={isActive}
                  className={[
                    'devtoolbar__tab',
                    isActive ? 'devtoolbar__tab--active' : '',
                    !tab.available ? 'devtoolbar__tab--disabled' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => tab.available && setActiveTab(tab.id)}
                  title={!tab.available ? `${tab.label} — coming soon` : tab.label}
                >
                  <Icon size={13} />
                  <span>{tab.label}</span>
                  {!tab.available && <span className="tab-soon-dot" title="Coming soon" />}
                </button>
              );
            })}
          </div>

          {/* Tab Body */}
          <div className="devtoolbar__panel-body" role="tabpanel">
            {currentTab?.available ? (
              activeTab === 'navigation' ? (
                <TabNavigation currentPath={currentPath} navigateTo={navigateTo} />
              ) : activeTab === 'storage' ? (
                <TabStorage />
              ) : null
            ) : (
              <TabComingSoon tab={currentTab} />
            )}
          </div>

        </div>
      )}

      {/* ── Trigger Tab — always visible, Excel-style ────────────────── */}
      <button
        type="button"
        className="devtoolbar__trigger"
        onClick={onToggle}
        aria-label={isOpen ? 'Collapse dev toolbar' : 'Open dev toolbar'}
        aria-expanded={isOpen}
      >
        <Bug size={14} />
        <span className="devtoolbar__trigger-label">Dev</span>
        <span className={`devtoolbar__trigger-chevron${isOpen ? ' devtoolbar__trigger-chevron--open' : ''}`}>
          ▲
        </span>
      </button>

    </div>
  );
}
